import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartMixed,
  faChevronDown,
  faChevronUp,
  faGear,
  faHandHoldingHeart,
  faHandshake,
  faMegaphone,
  faPalette,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group } from '@mantine/core';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { Heading, Nav, NavItem, NavMenuProvider, Pill } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import * as styles from './PrimaryNav.css';

interface ISubItem {
  isForwardedFrom?: string;
  label: string;
  to: string;
}

interface IPrimaryItem {
  icon: IconProp;
  label: string;
  matchPath: string;
  subItems?: ISubItem[];
  to: string;
}

function PrimaryNav(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? localUrl('/ia-prototype');
  const { pathname } = useLocation();

  const primary: IPrimaryItem[] = [
    {
      label: t('Marketing'),
      to: `${base}/marketing/all`,
      matchPath: `${base}/marketing`,
      icon: faMegaphone,
      subItems: [
        {
          label: t('All'),
          to: `${base}/marketing/all`,
          isForwardedFrom: `${base}/marketing`,
        },
        { label: t('Ads'), to: `${base}/marketing/ads` },
        { label: t('Email'), to: `${base}/marketing/email` },
        { label: t('Monetization'), to: `${base}/marketing/monetization` },
        { label: t('Other'), to: `${base}/marketing/other` },
        { label: t('Projects'), to: `${base}/marketing/projects` },
      ],
    },
    {
      label: t('Content'),
      to: `${base}/content/templates`,
      matchPath: `${base}/content`,
      icon: faPalette,
      subItems: [
        {
          label: t('Templates'),
          to: `${base}/content/templates`,
          isForwardedFrom: `${base}/content`,
        },
        { label: t('Creatives'), to: `${base}/content/creatives` },
        { label: t('Forms'), to: `${base}/content/forms` },
      ],
    },
    {
      label: t('Partner Marketing'),
      to: `${base}/partner-marketing/invites`,
      matchPath: `${base}/partner-marketing`,
      icon: faHandshake,
      subItems: [
        {
          label: t('Invites'),
          to: `${base}/partner-marketing/invites`,
          isForwardedFrom: `${base}/partner-marketing`,
        },
        { label: t('Partners'), to: `${base}/partner-marketing/partners` },
      ],
    },
    {
      label: t('Fundraising'),
      to: `${base}/fundraising/activity`,
      matchPath: `${base}/fundraising`,
      icon: faHandHoldingHeart,
      subItems: [
        {
          label: t('Activity'),
          to: `${base}/fundraising/activity`,
          isForwardedFrom: `${base}/fundraising`,
        },
        { label: t('Forms'), to: `${base}/fundraising/forms` },
        { label: t('Settings'), to: `${base}/fundraising/settings` },
      ],
    },
    {
      label: t('Audience'),
      to: `${base}/audience/people`,
      matchPath: `${base}/audience`,
      icon: faUsers,
      subItems: [
        {
          label: t('People'),
          to: `${base}/audience/people`,
          isForwardedFrom: `${base}/audience`,
        },
        { label: t('Segments'), to: `${base}/audience/segments` },
        { label: t('Imports'), to: `${base}/audience/imports` },
        { label: t('Conversions'), to: `${base}/audience/conversions` },
      ],
    },
    {
      label: t('Reporting'),
      to: `${base}/reporting`,
      matchPath: `${base}/reporting`,
      icon: faChartMixed,
    },
  ];

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    primary.forEach((item) => {
      if (item.subItems && pathname.startsWith(item.matchPath)) {
        initial.add(item.matchPath);
      }
    });
    return initial;
  });

  useEffect(() => {
    const matching = primary.find((item) => item.subItems && pathname.startsWith(item.matchPath));
    if (matching) {
      setExpandedSections((prev) => {
        if (prev.has(matching.matchPath)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(matching.matchPath);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function toggleSection(matchPath: string): void {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(matchPath)) {
        next.delete(matchPath);
      } else {
        next.add(matchPath);
      }
      return next;
    });
  }

  return (
    <NavMenuProvider>
      <Nav className={styles.root} label={t('Prototype primary navigation')} type={'vertical'}>
        <div className={styles.brand}>
          <Heading order={5} variant={'h5'}>
            {t('Feathr')}
          </Heading>
          <Pill theme={'orange'}>{t('Prototype')}</Pill>
        </div>
        <div className={styles.section}>
          {primary.map((item) => {
            if (!item.subItems) {
              return (
                <NavItem icon={item.icon} key={item.matchPath} to={item.to}>
                  {item.label}
                </NavItem>
              );
            }
            const expanded = expandedSections.has(item.matchPath);
            return (
              <React.Fragment key={item.matchPath}>
                <NavItem icon={item.icon} onClick={(): void => toggleSection(item.matchPath)}>
                  <Group gap={0} justify={'space-between'} w={'100%'} wrap={'nowrap'}>
                    {item.label}
                    <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                  </Group>
                </NavItem>
                {expanded && (
                  <Nav indented={true} label={`${item.label} navigation`} type={'vertical'}>
                    {item.subItems.map((sub) => (
                      <NavItem isForwardedFrom={sub.isForwardedFrom} key={sub.to} to={sub.to}>
                        {sub.label}
                      </NavItem>
                    ))}
                  </Nav>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className={styles.section}>
          <NavItem icon={faGear} to={`${base}/settings`}>
            {t('Settings')}
          </NavItem>
        </div>
      </Nav>
    </NavMenuProvider>
  );
}

export default PrimaryNav;
